import { ActionDialog } from '@e3dc-react/shell/Elements/Dialog'
import { ActionPositions, UpToThreeActions } from '@e3dc-react/shell/Elements/Dialog/ActionDialog/types'
import { Loading } from '@e3dc-react/shell/Elements/Loading'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Backdrop } from '@mui/material'
import { Box } from '@mui/system'
import { isUndefined } from 'lodash'
import React, { useState } from 'react'
import { WallboxConfigurationModel } from 'src/Libs/wallboxes'
import { WallboxStandardConfigurationDto } from 'src/Libs/wallboxes/dto/wallbox-configuration-update.dto'

type Props = {
  isOpen: boolean
  nonBidiActiveSunModeDisabledWallboxes: WallboxConfigurationModel[]
  handleClose: () => void
  updateSettings: (storageId: string, resourceId: string, model: WallboxStandardConfigurationDto, farmMasterStorageId?: string) => Promise<void>
}

/* A component that handles Sunmode state when toggling VDPP */
const SunModeDisabledDialog = ({ isOpen, nonBidiActiveSunModeDisabledWallboxes, handleClose, updateSettings }: Props): JSX.Element => {
  const { enqueueErrorStack } = useSnackbar()
  const { t } = useCustomTranslation()
  const [isLoading, setIsLoading] = useState(false)

  const handleActivateSunModes = async (): Promise<void> => {
    await Promise.all(
      nonBidiActiveSunModeDisabledWallboxes.map(async wallbox => {
        if (!isUndefined(wallbox.wallboxIndex) && !isUndefined(wallbox.storageSerialNo)) {
          return updateSettings(wallbox.storageSerialNo, wallbox.wallboxIndex.toString(), { sunModeActive: true })
        }
        return null
      })
    )
  }
  const handleIgnoreAction = (): void => {
    handleClose()
  }
  const handleActivateAction = async (): Promise<void> => {
    setIsLoading(true)
    try {
      await handleActivateSunModes()
      handleClose()
    } catch (error) {
      enqueueErrorStack(t('systemSettings-dynamic-prices.sunmode-disabled-dialog.actions.api.error'))
      console.error({ error })
    } finally {
      setIsLoading(false)
    }
  }

  const dialogActions: UpToThreeActions = [
    {
      name: t('systemSettings-dynamic-prices.sunmode-disabled-dialog.actions.ignore'),
      onClick: handleIgnoreAction,
      style: 'secondary',
      position: ActionPositions.Three,
      disabled: isLoading,
    },
    {
      name: t('systemSettings-dynamic-prices.sunmode-disabled-dialog.actions.activate'),
      onClick: handleActivateAction,
      style: 'primary',
      position: ActionPositions.Four,
      disabled: isLoading,
    },
  ]

  return (
    <ActionDialog
      open={isOpen}
      title={t('systemSettings-dynamic-prices.sunmode-disabled-dialog.title')}
      maxWidth="sm"
      actions={dialogActions}
      onClose={handleClose}
    >
      <Box mt={2}>{t('systemSettings-dynamic-prices.sunmode-disabled-dialog.description')}</Box>

      <Backdrop open={isLoading}>
        <Loading
          circularProgress={{
            sx: {
              marginBlock: 1,
            },
          }}
        />
      </Backdrop>
    </ActionDialog>
  )
}

export default SunModeDisabledDialog
